// Renters belong to markets (Oct 17, Felipe: "most users are in Santa Teresa
// unless stated contrary, users can be in multiple areas, but the area we
// show is governed by the domain").
//
// profiles.markets is kept by the database (renter_markets(),
// scripts/migrate/new-project-renter-markets-2026-10-17.sql): the market the
// person signed up on, plus every market that lists one of their areas, or
// the default market when that is empty. Matching a renter to a home:
//   - only renters in the home's market;
//   - only that market's areas count (as area preferences and as must-haves).
// Saving areas picked on one market's site replaces that market's areas and
// keeps the others.
//
// Deno mirror: supabase/functions/_shared/renterMarkets.ts (src/test/renterMarkets.test.ts
// runs the same cases against both).

export interface MarketAreas {
  id: string;
  areas: readonly string[];
  areaSlugs: readonly string[];
}

const norm = (s: string): string => s.trim().toLowerCase();

/** The market's display name for an area name or slug, or null when it isn't one of the market's. */
export function marketAreaName(area: string, market: MarketAreas): string | null {
  const k = norm(area);
  const i = market.areas.findIndex((name, j) => norm(name) === k || norm(market.areaSlugs[j] ?? "") === k);
  return i >= 0 ? market.areas[i] : null;
}

/**
 * Is this renter in the market of a home? A home without a market belongs to
 * the default market; a profile without markets (written before the database
 * stamped them) counts in the default market only.
 */
export function inMarket(
  markets: readonly string[] | null | undefined,
  tenantId: string | null | undefined,
  defaultTenantId: string,
): boolean {
  const t = tenantId || defaultTenantId;
  return markets && markets.length > 0 ? markets.includes(t) : t === defaultTenantId;
}

/** The renter's areas in one market, as that market's names ("playa-carmen" becomes "Playa Carmen"). */
export function areasForMarket(areas: readonly string[] | null | undefined, market: MarketAreas | null | undefined): string[] {
  if (!market || !areas?.length) return [];
  const out: string[] = [];
  for (const a of areas) {
    const name = marketAreaName(a, market);
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

/** Must-have tokens for matching in one market: "area:" tokens of other markets drop out, the rest stay. */
export function dealbreakersForMarket(tokens: readonly string[] | null | undefined, market: MarketAreas | null | undefined): string[] {
  const out: string[] = [];
  for (const t of tokens ?? []) {
    if (!t.startsWith("area:")) { out.push(t); continue; }
    const name = market ? marketAreaName(t.slice(5), market) : null;
    if (name && !out.includes(`area:${name}`)) out.push(`area:${name}`);
  }
  return out;
}

/**
 * The areas to store after the renter picked `picked` on this market's site:
 * the other markets' areas stay, this market's become `picked`.
 */
export function replaceMarketAreas(
  stored: readonly string[] | null | undefined,
  picked: readonly string[],
  market: MarketAreas | null | undefined,
): string[] {
  const kept = market ? (stored ?? []).filter((a) => marketAreaName(a, market) === null) : [];
  const out = [...kept];
  for (const a of picked) if (!out.some((x) => norm(x) === norm(a))) out.push(a);
  return out;
}

/** A renter's preferences read on one market's site: only that market's areas count. */
export function preferencesForSite<P extends { preferredAreas?: string[] | null; dealbreakers?: string[] | null }>(
  prefs: P,
  market: MarketAreas | null | undefined,
): P {
  return { ...prefs, preferredAreas: areasForMarket(prefs.preferredAreas, market), dealbreakers: dealbreakersForMarket(prefs.dealbreakers, market) };
}

/**
 * A renter's preferences as they apply to a home in `market`, or null when
 * the renter is not in that market. Generic over the two preference shapes
 * (the app's camelCase and the edge functions' snake_case).
 */
export function preferencesInMarket<P extends { preferredAreas?: string[] | null; dealbreakers?: string[] | null }>(
  prefs: P,
  markets: readonly string[] | null | undefined,
  market: MarketAreas | null | undefined,
  defaultTenantId: string,
): P | null {
  if (!inMarket(markets, market?.id, defaultTenantId)) return null;
  return preferencesForSite(prefs, market);
}
